import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Brain, AlertCircle, AlertTriangle, Info, Sparkles, Check, X, Loader2, RefreshCw,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const SEVERITY_META = {
  critical: { icon: AlertCircle, badgeClass: "bg-red-500/10 text-red-700 border-red-200", dotColor: "bg-red-500" },
  warning:  { icon: AlertTriangle, badgeClass: "bg-amber-500/10 text-amber-700 border-amber-200", dotColor: "bg-amber-500" },
  info:     { icon: Info, badgeClass: "bg-blue-500/10 text-blue-700 border-blue-200", dotColor: "bg-blue-500" },
};

const CATEGORY_LABEL = {
  kill_template: "Kill template",
  boost_template: "Boost template",
  follow_up: "Follow-up",
  send_window: "Send window",
  subject_pattern: "Subject pattern",
};

/**
 * Phase 5D — AI Coach widget for the Dashboard.
 * Shows active recommendations + Run Now + Apply/Ignore.
 */
export default function AICoachWidget() {
  const { t } = useTranslation();
  const [recs, setRecs] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [acting, setActing] = useState(null);

  const load = async () => {
    try {
      const [recsRes, statusRes] = await Promise.all([
        api.get("/ai-coach/recommendations", { params: { status: "new", limit: 10 } }),
        api.get("/ai-coach/_status"),
      ]);
      setRecs(recsRes.data || []);
      setStatus(statusRes.data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/ai-coach/run-now");
      if (data.sparse) {
        toast.info(t("ai_coach.sparse_msg", { count: data.count, min: 10 }));
      } else {
        toast.success(t("ai_coach.run_done", { count: data.count }));
      }
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRunning(false);
    }
  };

  const act = async (rec, action) => {
    setActing(rec.id);
    try {
      const url = `/ai-coach/${rec.id}/${action}`;
      const body = action === "ignore" ? { note: null } : {};
      await api.post(url, body);
      setRecs((prev) => prev.filter((r) => r.id !== rec.id));
      toast.success(action === "apply" ? t("ai_coach.applied") : t("ai_coach.ignored"));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setActing(null);
    }
  };

  return (
    <Card className="card-soft" data-testid="ai-coach-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Brain className="h-4 w-4 text-emerald-600" />
            {t("ai_coach.title")}
            {recs.length > 0 && (
              <Badge variant="secondary" className="text-[10px]">{recs.length}</Badge>
            )}
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={runNow}
            disabled={running}
            className="h-7 text-xs"
            data-testid="ai-coach-run-now"
          >
            {running
              ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
              : <RefreshCw className="h-3 w-3 mr-1.5" />}
            {t("ai_coach.analyze_now")}
          </Button>
        </div>
        {status?.last_run && (
          <p className="text-[11px] text-muted-foreground mt-1">
            {t("ai_coach.last_run")}: {new Date(status.last_run.started_at).toLocaleString()}
            {status.last_run.source === "cron" ? ` · ${t("ai_coach.auto")}` : ` · ${t("ai_coach.manual")}`}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-xs text-muted-foreground py-6 text-center">
            <Loader2 className="h-4 w-4 mx-auto animate-spin" />
          </div>
        ) : recs.length === 0 ? (
          <div className="py-6 text-center" data-testid="ai-coach-empty">
            <Sparkles className="h-5 w-5 mx-auto text-muted-foreground mb-2" />
            <p className="text-xs text-muted-foreground">{t("ai_coach.empty")}</p>
            <p className="text-[10px] text-muted-foreground mt-1">{t("ai_coach.empty_hint")}</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {recs.map((r) => {
              const meta = SEVERITY_META[r.severity] || SEVERITY_META.info;
              const Icon = meta.icon;
              return (
                <li
                  key={r.id}
                  className="p-3 rounded-md bg-muted/30 border hover:bg-muted/50 transition-colors"
                  data-testid={`ai-coach-rec-${r.id}`}
                >
                  <div className="flex items-start gap-2">
                    <span className={`mt-0.5 p-1 rounded ${meta.badgeClass} shrink-0`}>
                      <Icon className="h-3 w-3" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold">{r.title}</span>
                        <Badge variant="outline" className={`text-[9px] ${meta.badgeClass}`}>
                          {CATEGORY_LABEL[r.category] || r.category}
                        </Badge>
                        {r.confidence != null && (
                          <span className="text-[10px] text-muted-foreground">
                            · {Math.round(r.confidence * 100)}% {t("ai_coach.confidence")}
                          </span>
                        )}
                      </div>
                      {r.body && <p className="text-[12px] text-muted-foreground mt-1">{r.body}</p>}
                      <div className="flex items-center gap-1.5 mt-2">
                        <Button
                          size="sm" variant="default"
                          onClick={() => act(r, "apply")}
                          disabled={acting === r.id}
                          className="h-6 text-[11px] px-2"
                          data-testid={`ai-coach-apply-${r.id}`}
                        >
                          <Check className="h-3 w-3 mr-1" /> {t("ai_coach.apply")}
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => act(r, "ignore")}
                          disabled={acting === r.id}
                          className="h-6 text-[11px] px-2"
                          data-testid={`ai-coach-ignore-${r.id}`}
                        >
                          <X className="h-3 w-3 mr-1" /> {t("ai_coach.ignore")}
                        </Button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
